import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { DogSchool } from '@/data/dogSchools';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { generateSchoolIdFromParts } from '@/utils/idGenerator';

interface TableOfContentsProps {
  items?: Array<{ id: string; title: string }>;
  schools?: DogSchool[];
  defaultOpen?: boolean;
  title?: string;
}

interface CityGroup {
  city: string;
  country: string;
  schoolId: string;
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ 
  items,
  schools,
  defaultOpen = false
}) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (schools) {
      const uniqueGroups = schools.map(school => ({
        city: school.city,
        country: school.country,
        schoolId: school.id
      }));

      const sortedGroups = uniqueGroups.sort((a, b) => {
        if (a.country === b.country) {
          return a.city.localeCompare(b.city);
        }
        return a.country.localeCompare(b.country);
      });

      setCityGroups(sortedGroups);
    }
  }, [schools]);

  const scrollToSection = (id: string) => {
    console.log('Scrolling to section:', id);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveId(id);
      if (isOpen) {
        setIsOpen(false);
      }
    } else {
      console.error('Element not found:', id);
    }
  };

  if ((!schools || schools.length === 0) && (!items || items.length === 0)) {
    return null;
  }

  return (
    <Card className="p-4 sticky top-20 bg-white/80 backdrop-blur-sm z-10">
      <Button
        variant="ghost"
        className="w-full flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium">
          {language === 'de' ? 'Inhaltsverzeichnis' : 'Table of Contents'}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </Button>
      
      {isOpen && (
        <motion.div 
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-2"
        >
          <ul className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
            {items && items.length > 0 && (
              items.map((item, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className={`text-gray-700 hover:text-primary hover:underline text-left w-full py-1.5 px-2 rounded-md transition-colors hover:bg-gray-100 ${activeId === item.id ? 'bg-gray-200' : ''}`}
                  >
                    {item.title}
                  </button>
                </li>
              ))
            )}
            
            {schools && schools.length > 0 && cityGroups.map((group, index) => {
              const cityId = generateSchoolIdFromParts(group.city, group.country, group.schoolId);
              return (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(cityId)}
                    className={`text-gray-700 hover:text-primary hover:underline text-left w-full py-1.5 px-2 rounded-md transition-colors hover:bg-gray-100 ${activeId === cityId ? 'bg-gray-200' : ''}`}
                  >
                    {language === 'de' 
                      ? `Hundeschulen in ${group.city}, ${group.country === 'Germany' ? 'Deutschland' : group.country === 'Austria' ? 'Österreich' : 'Schweiz'}`
                      : `Dog Schools in ${group.city}, ${group.country}`
                    }
                  </button>
                </li>
              );
            })}
          </ul>
        </motion.div>
      )}
    </Card>
  );
};
